export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  fbc?: string;
  utm_id?: string;
  utm_adgroup?: string;
  // Allow additional UTM parameters with an index signature
  [key: string]: string | undefined;
}

export function extractUTMParams(url: string): UTMParams {
  try {
    const parsedUrl = new URL(url);
    const params = parsedUrl.searchParams;

    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_term: params.get('utm_term') || undefined,
      utm_content: params.get('utm_content') || undefined,
      utm_id: params.get('utm_id') || undefined,
      utm_adgroup: params.get('utm_adgroup') || undefined,
      gclid: params.get('gclid') || undefined,
      fbclid: params.get('fbclid') || undefined,
      fbc: params.get('fbc') || undefined,
    };
  } catch (error) {
    return {};
  }
}

export function extractUTMParamsFromReferrer(): UTMParams {
  if (typeof document === 'undefined') return {};
  return extractUTMParams(document.referrer);
}

export function sanitizeUtmValue(value?: string): string | undefined {
  if (!value) return undefined;
  // Allow alphanumeric, dash, underscore, period, slash, and space. Truncate to 255 chars.
  return value.replace(/[^\w\-./\s]/g, '').substring(0, 255);
}

export function sanitizeUTMParams(params: UTMParams): UTMParams {
  // Start with standard parameters
  const sanitized: UTMParams = {
    utm_source: sanitizeUtmValue(params.utm_source),
    utm_medium: sanitizeUtmValue(params.utm_medium),
    utm_campaign: sanitizeUtmValue(params.utm_campaign),
    utm_term: sanitizeUtmValue(params.utm_term),
    utm_content: sanitizeUtmValue(params.utm_content),
    utm_id: sanitizeUtmValue(params.utm_id),
    utm_adgroup: sanitizeUtmValue(params.utm_adgroup),
    gclid: sanitizeUtmValue(params.gclid),
    fbclid: sanitizeUtmValue(params.fbclid),
    fbc: sanitizeUtmValue(params.fbc),
  };

  // Process any additional utm_* parameters
  Object.keys(params).forEach((key) => {
    if (key.startsWith('utm_') && sanitized[key] === undefined) {
      sanitized[key] = sanitizeUtmValue(params[key]);
    }
  });

  return sanitized;
}

/**
 * Converts fbclid to fbc format for Facebook Conversions API
 * Format: fb.{version}.{subdomainIndex}.{creationTime}.{fbclid}
 * @param fbclid The Facebook Click ID
 * @param subdomainIndex The subdomain index (default: 1)
 * @param timestamp The creation time (default: current timestamp)
 * @returns The formatted fbc parameter
 */
export function convertFbclidToFbc(
  fbclid: string,
  subdomainIndex = 1,
  timestamp = Date.now(),
): string {
  const version = 1;
  return `fb.${version}.${subdomainIndex}.${timestamp}.${fbclid}`;
}

export function storeUTMParams(utmParams: UTMParams): void {
  if (typeof localStorage === 'undefined') return;
  const sanitized = sanitizeUTMParams(utmParams);
  localStorage.setItem('utm_params', JSON.stringify(sanitized));
}

export function getStoredUTMParams(): UTMParams {
  if (typeof localStorage === 'undefined') return {};
  try {
    const stored = localStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    return {};
  }
}

/**
 * Campaign configuration interface for detecting specific marketing campaigns
 */
export interface CampaignConfig {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_id: string;
}

/**
 * Detects if a specific campaign is active based on UTM parameters from multiple sources
 * @param campaignConfig The campaign configuration to check for
 * @param urlUtmParams Optional UTM parameters from current URL
 * @param storedUtmParams Optional UTM parameters from localStorage
 * @param cookieUtmParams Optional UTM parameters from cookies
 * @returns True if the campaign is detected, false otherwise
 */
export function detectCampaign(
  campaignConfig: CampaignConfig,
  urlUtmParams?: UTMParams,
  storedUtmParams?: UTMParams,
  cookieUtmParams?: UTMParams,
): boolean {
  try {
    // Helper function to check if params match campaign
    const checkParams = (params: UTMParams): boolean =>
      params.utm_source === campaignConfig.utm_source &&
      params.utm_medium === campaignConfig.utm_medium &&
      params.utm_campaign === campaignConfig.utm_campaign &&
      params.utm_id === campaignConfig.utm_id;

    // Check URL params first (highest priority)
    if (urlUtmParams && checkParams(urlUtmParams)) {
      return true;
    }

    // Check localStorage params
    if (storedUtmParams && checkParams(storedUtmParams)) {
      return true;
    }

    // Check cookie params last
    if (cookieUtmParams && checkParams(cookieUtmParams)) {
      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
}

/**
 * Detects if the Aetna Member NL campaign is active
 * @param urlUtmParams Optional UTM parameters from current URL
 * @param storedUtmParams Optional UTM parameters from localStorage
 * @param cookieUtmParams Optional UTM parameters from cookies
 * @returns True if the Aetna Member NL campaign is detected, false otherwise
 */
export function detectAetnaMemberNLCampaign(
  urlUtmParams?: UTMParams,
  storedUtmParams?: UTMParams,
  cookieUtmParams?: UTMParams,
): boolean {
  const aetnaCampaignConfig: CampaignConfig = {
    utm_source: 'NL',
    utm_medium: 'email',
    utm_campaign: 'Aetna_MemberNL',
    utm_id: 'Aetna_MemberNL',
  };

  return detectCampaign(
    aetnaCampaignConfig,
    urlUtmParams,
    storedUtmParams,
    cookieUtmParams,
  );
}
